import type { Accept } from "react-dropzone";

export enum AssetType {
  Video = "video",
  Image = "image",
  Document = "doc",
}

const assetTypes = Object.values(AssetType);

export const videoAccept: Readonly<Accept> = {
  "video/mp4": [".mp4"],
  "video/quicktime": [".mov"],
};

export const imageAccept: Readonly<Accept> = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
};

export const documentAccept: Readonly<Accept> = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "application/pkcs7-mime": [".p7m"],
};

const assetTypesAccept: Partial<Record<AssetType, Readonly<Accept>>> = {
  [AssetType.Video]: videoAccept,
  [AssetType.Image]: imageAccept,
  [AssetType.Document]: documentAccept,
};

interface DocumentType {
  id: AssetType;
  value: string;
  uppercaseExtensions: readonly string[];
}

export const documentTypes: ReadonlyArray<DocumentType> = [
  {
    id: AssetType.Video,
    value: "Video",
    uppercaseExtensions: getUppercaseExtensions(getAssetTypesAcceptExtensions([AssetType.Video])),
  },
  {
    id: AssetType.Image,
    value: "Immagine",
    uppercaseExtensions: getUppercaseExtensions(getAssetTypesAcceptExtensions([AssetType.Image])),
  },
  {
    id: AssetType.Document,
    value: "Documento",
    uppercaseExtensions: getUppercaseExtensions(getAssetTypesAcceptExtensions([AssetType.Document])),
  },
];

const ALL_FILE_TYPES_ACCEPT: Accept = { ...imageAccept, ...documentAccept };

export function getAssetTypesAccept(assetTypes: ReadonlyArray<AssetType>): Readonly<Accept> {
  if (assetTypes.length === 0) return ALL_FILE_TYPES_ACCEPT;
  const accepts = assetTypes.map((assetType) => assetTypesAccept[assetType] ?? {});
  const entries = accepts.flatMap((accept) => Object.entries(accept));
  return Object.fromEntries(entries);
}

export function getAssetTypesAcceptExtensions(assetTypes: ReadonlyArray<AssetType>): ReadonlyArray<string> {
  const accept = getAssetTypesAccept(assetTypes);
  return Object.values(accept).flat();
}

export const defaultAssetTypes: ReadonlyArray<AssetType> = [AssetType.Image, AssetType.Document];

export function getAssetTypesFromDocumentStructureType(type: string | undefined | null): ReadonlyArray<AssetType> {
  const documentStructureAssetTypes = (type ?? "")
    .split(",")
    .map((v) => v.trim().toLowerCase())
    .filter((v): v is AssetType => assetTypes.includes(v as AssetType));

  if (documentStructureAssetTypes.length === 0) {
    return defaultAssetTypes;
  }

  return documentStructureAssetTypes;
}

export function getDocumentStructureTypeFromAssetTypes(assetTypes: ReadonlyArray<AssetType>): string {
  return assetTypes.join(",");
}

export function getUppercaseExtensions(extensions: ReadonlyArray<string>): ReadonlyArray<string> {
  return extensions.map((v) => (v.startsWith(".") ? v.slice(1).toUpperCase() : v.toUpperCase()));
}

export function getImageMimeTypes(): ReadonlyArray<string> {
  return Object.keys(imageAccept);
}

export function isValidImageFile(file: File): boolean {
  const allowedImageTypes = getImageMimeTypes();
  return allowedImageTypes.includes(file.type);
}
